import {
	faBuilding,
	faCartShopping,
	faCheck,
	faChevronDown,
	faChevronUp,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalCartDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { clientDisplayId } from '../lib/clientDisplayId';
import { formatCurrency } from '../lib/formatCurrency';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';

/**
 * Client/location selection page.
 *
 * Two-step flow matching v1.0:
 * 1. User clicks a client to select it
 * 2. User clicks "Choose Location" to confirm the switch
 *
 * Each account card shows the full street address and — when that
 * account has an open cart — a compact summary (total + item count)
 * on the right with a chevron that expands the line items inside the
 * card. Collapsed by default: carts can be enormous, so the expanded
 * list is height-capped and scrolls (PD-14079 review feedback).
 *
 * The current client is highlighted. Clicking the current client
 * just goes back (no switch needed).
 */
export function ClientSelectorPage(): JSX.Element {
	const { t } = useTranslation();
	const {
		isAuthenticated,
		needsClientSelection,
		availableClients,
		activeClientId,
		selectClient,
		isLoading,
		logout,
		token,
	} = usePortalAuth();
	const currentCart = useCartStore((s) => s.cart);

	const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
	const [previewByClientId, setPreviewByClientId] = useState<
		Record<number, PortalCartDto | null>
	>({});
	const [previewsLoading, setPreviewsLoading] = useState(false);
	const [expandedClientId, setExpandedClientId] = useState<number | null>(null);
	const [confirming, setConfirming] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [justSelected, setJustSelected] = useState(false);

	// Default the selected client to the current active one
	useEffect(() => {
		if (activeClientId != null && selectedClientId == null) {
			setSelectedClientId(activeClientId);
		}
	}, [activeClientId, selectedClientId]);

	// Load every account's cart summary up front so each card can show its
	// total + item count. The previews are small (line descriptions only)
	// and the per-account endpoint validates assignment server-side. The
	// active client's cart comes from the store — no extra fetch.
	const previewsRequested = useRef(false);
	useEffect(() => {
		if (token == null || availableClients.length === 0) {
			return;
		}
		if (previewsRequested.current) {
			return;
		}
		previewsRequested.current = true;

		setPreviewsLoading(true);
		void Promise.all(
			availableClients.map(async (client) => {
				if (client.id === activeClientId) {
					return [client.id, currentCart] as const;
				}
				try {
					const res = await cartService.previewCart(token, client.id);
					return [client.id, res.data] as const;
				} catch {
					return [client.id, null] as const;
				}
			}),
		)
			.then((entries) => {
				setPreviewByClientId(Object.fromEntries(entries));
			})
			.finally(() => {
				setPreviewsLoading(false);
			});
	}, [token, availableClients, activeClientId, currentCart]);

	const handleClientClick = useCallback((clientId: number) => {
		setSelectedClientId(clientId);
		setError(null);
	}, []);

	// Confirm location switch
	const handleConfirm = useCallback(async () => {
		if (selectedClientId == null) return;

		// Same client — just go back
		if (selectedClientId === activeClientId) {
			setJustSelected(true);
			return;
		}

		setConfirming(true);
		setError(null);
		try {
			await selectClient(selectedClientId);
			setJustSelected(true);
		} catch (err: unknown) {
			const message =
				err instanceof Error ? err.message : t('Failed to select client');
			setError(t(message));
			setConfirming(false);
		}
	}, [selectedClientId, activeClientId, selectClient, t]);

	// --- Early returns AFTER all hooks ---

	// Redirect home after a successful confirmation
	if (justSelected && isAuthenticated && !needsClientSelection) {
		return <Navigate to="/" replace />;
	}

	// Not logged in
	if (!isAuthenticated && !needsClientSelection) {
		return <Navigate to="/" replace />;
	}

	return (
		<div className="min-h-dvh bg-brand-page-bg px-4 py-8">
			<div className="w-full max-w-md mx-auto">
				<div className="text-center mb-6">
					<h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
						{t('Select Account')}
					</h1>
					<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
						{t('Choose which account you would like to access.')}
					</p>
				</div>

				{error != null && (
					<div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3">
						{error}
					</div>
				)}

				{/* Account cards: address + collapsed cart summary per card,
				    alphabetical by name */}
				<div className="space-y-2 mb-4">
					{[...availableClients]
						.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
						.map((client) => {
							const isCurrent = client.id === activeClientId;
							const isSelected = client.id === selectedClientId;
							const isExpanded = client.id === expandedClientId;
							const preview = previewByClientId[client.id] ?? null;
							const lineItems = preview?.lineItems ?? [];
							const hasCart = lineItems.length > 0;

							return (
								<div
									key={client.id}
									className={`bg-brand-surface border rounded-xl transition-all ${
										isSelected
											? 'border-portal-accent ring-1 ring-portal-accent'
											: isCurrent
												? 'border-portal-accent/50'
												: 'border-brand-border'
									}`}
								>
									<div className="flex items-center gap-3 px-4 py-3.5">
										<button
											type="button"
											onClick={() => {
												handleClientClick(client.id);
											}}
											disabled={isLoading || confirming}
											className="flex items-center gap-3 flex-1 min-w-0 text-left min-h-touch disabled:opacity-50"
										>
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
													isCurrent
														? 'bg-portal-accent dark:bg-portal-accent/100'
														: 'bg-portal-accent/20'
												}`}
											>
												<FontAwesomeIcon
													icon={isCurrent ? faCheck : faBuilding}
													className={
														isCurrent ? 'text-white' : 'text-portal-accent'
													}
												/>
											</div>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
													{t(client.name)}
												</div>
												{client.address1 != null && client.address1 !== '' && (
													<div className="text-xs text-brand-muted truncate">
														{t(client.address1)}
														{client.address2 != null &&
															client.address2 !== '' &&
															`, ${t(client.address2)}`}
													</div>
												)}
												{client.city != null && client.state != null && (
													<div className="text-xs text-brand-muted truncate">
														{t(client.city)}, {t(client.state)}
														{client.zip != null && client.zip !== ''
															? ` ${t(client.zip)}`
															: ''}
													</div>
												)}
												{/* Customer-facing id — external id, falling back to
											    the internal id like other identity displays */}
												<div className="text-xs text-brand-muted truncate">
													{t('ID')}: {t(clientDisplayId(client))}
												</div>
											</div>
										</button>

										{/* Right rail: current badge + collapsed cart summary */}
										<div className="flex flex-col items-end gap-1 shrink-0">
											{isCurrent && (
												<span className="text-xs text-portal-accent font-medium">
													{t('Current')}
												</span>
											)}
											{previewsLoading && !hasCart ? (
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin text-gray-300 dark:text-gray-600 text-xs"
												/>
											) : hasCart ? (
												<button
													type="button"
													onClick={() => {
														setExpandedClientId(isExpanded ? null : client.id);
													}}
													aria-expanded={isExpanded}
													aria-label={t('Toggle cart details')}
													className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-portal-accent min-h-touch"
												>
													<FontAwesomeIcon
														icon={faCartShopping}
														className="text-gray-400 text-[11px]"
													/>
													<span className="font-medium">
														{formatCurrency(preview?.subtotal)}
													</span>
													<span className="text-brand-muted">
														·{' '}
														{lineItems.length === 1
															? t('1 item')
															: `${lineItems.length} ${t('items')}`}
													</span>
													<FontAwesomeIcon
														icon={isExpanded ? faChevronUp : faChevronDown}
														className="text-[10px] text-gray-400"
													/>
												</button>
											) : null}
										</div>
									</div>

									{/* Expanded cart line items — height-capped, scrolls for
								    big carts so the card never takes over the page */}
									{isExpanded && hasCart && (
										<div className="px-4 pb-3">
											<div className="bg-brand-input border border-brand-border rounded-lg p-3 max-h-48 overflow-y-auto">
												<div className="space-y-0.5 mb-1.5">
													{lineItems.map((item) => (
														<div
															key={item.id}
															className="flex justify-between text-xs text-gray-600 dark:text-gray-400"
														>
															<span className="truncate flex-1 mr-2">
																{t(item.description)}{' '}
																<span className="text-gray-400">
																	x{item.quantityOrdered}
																</span>
															</span>
															<span className="shrink-0">
																{formatCurrency(item.subtotal)}
															</span>
														</div>
													))}
												</div>
												<div className="flex justify-between text-xs font-medium text-gray-900 dark:text-gray-100 pt-1.5 border-t border-brand-border sticky bottom-0">
													<span>{t('Subtotal')}</span>
													<span>{formatCurrency(preview?.subtotal)}</span>
												</div>
											</div>
										</div>
									)}
								</div>
							);
						})}
				</div>

				{/* Confirm button */}
				<button
					type="button"
					onClick={() => void handleConfirm()}
					disabled={selectedClientId == null || isLoading || confirming}
					className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-4"
				>
					{confirming ? (
						<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
					) : null}
					{confirming ? t('Switching...') : t('Choose Location')}
				</button>

				<div className="text-center">
					<button
						type="button"
						onClick={logout}
						className="text-sm text-brand-muted hover:text-gray-700 dark:hover:text-gray-200 underline"
					>
						{t('Sign out')}
					</button>
				</div>
			</div>
		</div>
	);
}
